// ===========================================
// Gamification Types (Frontend)
// ===========================================
// Types for gamification features - progress, achievements, pieces, skills

// ===========================================
// ENUMS
// ===========================================

export type AchievementType =
  | 'ATTENDANCE_MILESTONE'
  | 'LEVEL_COMPLETION'
  | 'STREAK'
  | 'PERFECT_TERM'
  | 'FIRST_RECITAL'
  | 'EXAM_PASSED'
  | 'PIECE_MASTERED'
  | 'SPECIAL';

export type SkillStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

// ===========================================
// GAMIFICATION LEVEL INFO (from ProgramLevel)
// ===========================================

export interface GamificationLevelInfo {
  id: string;
  name: string;
  displayName: string | null;
  threshold: number | null;
  mascot: string | null;
  mascotEmoji: string | null;
  color: string | null;
  sortOrder: number;
}

// ===========================================
// STUDENT PROGRESS
// ===========================================

/**
 * A progress row as `GET /gamification/progress` sends it: the raw Prisma row with
 * `progressInclude = { Student, ProgramLevel }`, so relation keys are PascalCase.
 * This type said `currentLevel`/`student` until 2026-09-29 — keys that never arrived,
 * so the admin Student Progress page dropped every row (#201). The single-student
 * endpoint is different: it maps to `ProgressStats` (camelCase `currentLevel`).
 */
export interface StudentProgress {
  id: string;
  studentId: string;
  schoolId: string;
  currentLevelId: string;
  ProgramLevel: GamificationLevelInfo;
  levelProgress: number;
  totalLessonsAttended: number;
  currentStreak: number;
  longestStreak: number;
  totalPiecesAssigned: number;
  totalPiecesCompleted: number;
  totalSkillsCompleted: number;
  lastAttendanceDate: string | null;
  lastStreakCheckDate: string | null;
  levelsCompleted: Array<{ levelId: string; levelName: string; completedAt: string }> | null;
  createdAt: string;
  updatedAt: string;
  /** The full Student row (`Student: true`); only the fields read here are typed. */
  Student: {
    id: string;
    firstName: string;
    lastName: string;
    /** `@db.Date` — render with UTC getters; 1 January = "unknown" (isPlaceholderBirthDate). */
    birthDate: string;
  };
}

/** `POST /gamification/progress/:studentId/advance-level` returns BOTH, not a bare progress row. */
export interface AdvanceLevelResult {
  progress: StudentProgress;
  newLevel: { id: string; name: string; displayName: string | null };
}

export interface ProgressStats {
  currentLevel: GamificationLevelInfo;
  levelProgress: number;
  totalLessonsAttended: number;
  attendanceRate: number;
  currentStreak: number;
  longestStreak: number;
  totalPiecesCompleted: number;
  totalSkillsCompleted: number;
  levelsCompleted: Array<{ levelId: string; levelName: string; completedAt: string }>;
}

export interface LeaderboardEntry {
  rank: number;
  studentId: string;
  studentName: string;
  currentLevel: GamificationLevelInfo;
  totalLessonsAttended: number;
  currentStreak: number;
  totalPiecesCompleted: number;
}

export interface LeaderboardParams {
  sortBy?: 'lessons' | 'streak' | 'pieces' | 'level';
  limit?: number;
}

// ===========================================
// ACHIEVEMENTS
// ===========================================

export interface AchievementDefinition {
  id: string;
  schoolId: string;
  type: AchievementType;
  name: string;
  description: string;
  threshold: number;
  badgeIcon: string;
  badgeColor: string;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Achievement {
  id: string;
  studentId: string;
  schoolId: string;
  achievementDefinitionId: string | null;
  name: string;
  description: string;
  badgeIcon: string;
  badgeColor: string;
  type: AchievementType;
  metadata: Record<string, unknown> | null;
  earnedAt: string;
  createdAt: string;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  achievementDefinition?: AchievementDefinition;
}

export interface CreateAchievementDefinitionData {
  type: AchievementType;
  name: string;
  description: string;
  threshold: number;
  badgeIcon: string;
  badgeColor: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateAchievementDefinitionData {
  name?: string;
  description?: string;
  threshold?: number;
  badgeIcon?: string;
  badgeColor?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface AwardAchievementData {
  achievementDefinitionId: string;
  metadata?: Record<string, unknown>;
}

export interface AwardSpecialAchievementData {
  name: string;
  description: string;
  badgeIcon: string;
  badgeColor: string;
  metadata?: Record<string, unknown>;
}

export interface AchievementDefinitionFilters {
  type?: AchievementType;
  isActive?: boolean;
}

export interface StudentAchievementFilters {
  type?: AchievementType;
  page?: number;
  limit?: number;
}

export interface RecentAchievementsFilters {
  days?: number;
  limit?: number;
}

// ===========================================
// PIECES & SKILLS
// ===========================================

export interface Piece {
  id: string;
  schoolId: string;
  title: string;
  composer: string | null;
  description: string | null;
  difficulty: number;
  programLevelId: string | null;
  instrumentId: string | null;
  genre: string | null;
  gradeLevel: string | null;
  sheetMusicUrl: string | null;
  audioSampleUrl: string | null;
  lessonPlanUrl: string | null;
  lessonPlanName: string | null;
  lessonPlanProvider: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  skills?: PieceSkill[];
  instrument?: {
    id: string;
    name: string;
  };
  programLevel?: {
    id: string;
    name: string;
    color: string | null;
  };
  _count?: {
    skills: number;
    studentPieces: number;
  };
}

export interface PieceSkill {
  id: string;
  pieceId: string;
  schoolId: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
  lessonPlanUrl: string | null;
  lessonPlanName: string | null;
  lessonPlanProvider: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePieceData {
  title: string;
  composer?: string;
  description?: string;
  difficulty?: number;
  programLevelId?: string;
  instrumentId?: string;
  genre?: string;
  gradeLevel?: string;
  sheetMusicUrl?: string;
  audioSampleUrl?: string;
  lessonPlanUrl?: string;
  lessonPlanName?: string;
  lessonPlanProvider?: string;
  isActive?: boolean;
  autoCreateSkills?: boolean;
  skills?: { name: string; color?: string | null }[];
}

export interface UpdatePieceData {
  title?: string;
  composer?: string | null;
  description?: string | null;
  difficulty?: number;
  programLevelId?: string | null;
  instrumentId?: string | null;
  genre?: string | null;
  gradeLevel?: string | null;
  sheetMusicUrl?: string | null;
  audioSampleUrl?: string | null;
  lessonPlanUrl?: string | null;
  lessonPlanName?: string | null;
  lessonPlanProvider?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface PieceFilters {
  instrumentId?: string;
  difficulty?: number;
  programLevelId?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateSkillData {
  name: string;
  description?: string;
  icon?: string;
  color?: string | null;
  sortOrder?: number;
  lessonPlanUrl?: string;
  lessonPlanName?: string;
  lessonPlanProvider?: string;
}

export interface UpdateSkillData {
  name?: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  sortOrder?: number;
  lessonPlanUrl?: string | null;
  lessonPlanName?: string | null;
  lessonPlanProvider?: string | null;
}

export interface ReorderSkillsData {
  skillIds: string[];
}

// ===========================================
// STUDENT PIECES (ASSIGNMENTS)
// ===========================================

export interface StudentPiece {
  id: string;
  studentId: string;
  pieceId: string;
  schoolId: string;
  assignedById: string;
  startedAt: string | null;
  completedAt: string | null;
  isCompleted: boolean;
  teacherNotes: string | null;
  studentNotes: string | null;
  createdAt: string;
  updatedAt: string;
  piece?: Piece;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  skillProgress?: StudentPieceSkill[];
}

export interface StudentPieceSkill {
  id: string;
  studentPieceId: string;
  pieceSkillId: string;
  schoolId: string;
  status: SkillStatus;
  startedAt: string | null;
  completedAt: string | null;
  completedById: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  pieceSkill?: PieceSkill;
  completedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface AssignPieceData {
  pieceId: string;
  teacherNotes?: string;
}

export interface UpdateSkillProgressData {
  status: SkillStatus;
  notes?: string;
}

export interface StudentPieceFilters {
  isCompleted?: boolean;
  page?: number;
  limit?: number;
}

export interface ProgressSummary {
  studentPieceId: string;
  pieceTitle: string;
  totalSkills: number;
  completedSkills: number;
  inProgressSkills: number;
  notStartedSkills: number;
  completionPercentage: number;
  isCompleted: boolean;
}

// ===========================================
// CONSTANTS
// ===========================================

export const ACHIEVEMENT_ICONS: Record<string, string> = {
  star: '⭐',
  trophy: '🏆',
  medal: '🏅',
  crown: '👑',
  flame: '🔥',
  fire: '🔥',
  rocket: '🚀',
  diamond: '💎',
  'check-circle': '✅',
  'graduation-cap': '🎓',
  mic: '🎤',
  certificate: '📜',
  music: '🎵',
};

// ===========================================
// API RESPONSE TYPES
// ===========================================

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ===========================================
// LESSON PIECES (CLASS ASSIGNMENTS)
// ===========================================

export interface LessonPiece {
  id: string;
  lessonId: string;
  pieceId: string;
  schoolId: string;
  isCurrent: boolean;
  assignedById: string;
  assignedAt: string;
  createdAt: string;
  updatedAt: string;
  piece?: Piece;
  assignedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface StudentLessonPieceItem {
  lessonPiece: {
    id: string;
    isCurrent: boolean;
    assignedAt: string;
  };
  studentPiece: {
    id: string;
    isCompleted: boolean;
    completedAt: string | null;
    startedAt: string | null;
    skillProgress: Array<{
      id: string;
      status: SkillStatus;
      pieceSkill: { id: string; name: string; sortOrder: number; color: string | null };
    }>;
  } | null;
  piece: {
    id: string;
    title: string;
    composer: string | null;
    difficulty: number | null;
    skills: Array<{ id: string; name: string; sortOrder: number; color: string | null }>;
  };
  lesson: { id: string; name: string | null };
}

export interface StudentLessonPiecesResult {
  currentPieces: StudentLessonPieceItem[];
  pastPieces: StudentLessonPieceItem[];
}

// ===========================================
// CLASS PIECE PROGRESS (Teacher View)
// ===========================================

export interface ClassPieceProgressStudent {
  student: {
    id: string;
    firstName: string;
    lastName: string;
  };
  studentPiece: {
    id: string;
    isCompleted: boolean;
    completedAt: string | null;
    startedAt: string | null;
  } | null;
  skillProgress: Array<{
    id: string;
    status: SkillStatus;
    pieceSkillId: string;
    startedAt: string | null;
    completedAt: string | null;
    notes: string | null;
    pieceSkill: {
      id: string;
      name: string;
      sortOrder: number;
      color: string | null;
    };
  }>;
}

export interface ClassPieceProgressResult {
  piece: {
    id: string;
    title: string;
    composer: string | null;
    skills: Array<{
      id: string;
      name: string;
      sortOrder: number;
      color: string | null;
    }>;
  };
  students: ClassPieceProgressStudent[];
}
